// Backlog 071-A — best-effort Cloud Build log tail on cloud_build failure.
//
// When `grid plug` fails inside the cloud_build step, the actionable
// detail is always one round-trip away in `gcloud builds log <build-id>`. This
// helper closes that gap — when we can identify the build_id from the trace,
// we shell out to gcloud and surface the last ~30 lines below the failed step
// line. When we can't (gcloud missing, no permissions, no parsable build_id),
// we render the full Cloud Build console URL so the user can click through.
//
// All shell-outs go through `runGcloudBuildsLog` so tests can inject a mock.

import type { DeployTrace } from './plug-poll.js';

/** Cloud Build IDs are UUID-shaped. */
const BUILD_ID_RE =
  /\b([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\b/i;

/** Default Google Cloud project — matches the deployer's project (US production). */
export const DEFAULT_GCLOUD_PROJECT = 'unipier-ai';

/** Number of trailing log lines we render inline. */
export const DEFAULT_TAIL_LINES = 30;

export interface BuildIdResolution {
  buildId: string | null;
  /**
   * If the trace's failure error string carries a Cloud Build console URL,
   * surface it as a click-through fallback when gcloud is unavailable.
   */
  consoleUrl: string | null;
}

/**
 * Pull a build_id (and optionally a console URL) out of the deploy trace.
 *
 * Looks at, in order:
 *   1. `result.build_id` — populated only on success today, but kept as a
 *      forward-compat hook in case the API later attaches it to `error`.
 *   2. The cloud_build step's `error` field — the deployer wraps Cloud Build
 *      failures with `... Logs: <logUrl>`, where `<logUrl>` is a console URL
 *      that contains the build UUID.
 *   3. `error.message` on the trace top-level — same content as (2), repeated
 *      by the deployer's outer catch.
 *
 * Returns nulls when nothing is parseable. Callers should still render the
 * fallback ("full log: gcloud builds log ...") when buildId is non-null but
 * the gcloud shell-out itself fails.
 */
export function resolveBuildId(trace: DeployTrace): BuildIdResolution {
  // (1) result.build_id (forward-compat).
  const fromResult = (trace.result as { build_id?: unknown } | null)?.build_id;
  if (typeof fromResult === 'string' && fromResult.length > 0) {
    return { buildId: fromResult, consoleUrl: null };
  }

  // (2) failed step's error string.
  const failedStep = trace.steps.find(
    (s) => s.status === 'failed' && s.step === 'cloud_build',
  );
  const stepErr =
    typeof failedStep?.error === 'string' ? failedStep.error : '';
  // (3) trace error message — same content, repeated.
  const topErr = trace.error?.message ?? '';

  let foundUrl: string | null = null;
  for (const candidate of [stepErr, topErr]) {
    if (!candidate) continue;
    const m = BUILD_ID_RE.exec(candidate);
    if (m) {
      const buildId = m[1].toLowerCase();
      const urlMatch = /https?:\/\/[^\s]+/.exec(candidate);
      return { buildId, consoleUrl: urlMatch ? urlMatch[0] : null };
    }
    if (!foundUrl) {
      const urlMatch = /https?:\/\/[^\s]+/.exec(candidate);
      if (urlMatch) foundUrl = urlMatch[0];
    }
  }

  return { buildId: null, consoleUrl: foundUrl };
}

/**
 * Adapter signature for shelling out to gcloud. Returns the captured stdout
 * on success, or null when the binary is missing / errored / produced no
 * output. Implementations MUST NOT throw — the surface here is best-effort.
 */
export type GcloudBuildsLogFn = (
  buildId: string,
  projectId: string,
) => Promise<string | null>;

/**
 * Real adapter: shells out to `gcloud builds log <id> --project=<p>`. Returns
 * null on any failure (missing binary, permissions, network). Output capped
 * by Node's child_process buffer; tail is computed by the caller.
 */
export const realGcloudBuildsLog: GcloudBuildsLogFn = async (
  buildId,
  projectId,
) => {
  const { spawn } = await import('node:child_process');
  return new Promise<string | null>((resolve) => {
    let resolved = false;
    let proc: ReturnType<typeof spawn>;
    try {
      proc = spawn('gcloud', ['builds', 'log', buildId, `--project=${projectId}`], {
        stdio: ['ignore', 'pipe', 'pipe'],
      });
    } catch {
      resolve(null);
      return;
    }
    let stdout = '';
    proc.stdout?.on('data', (chunk: Buffer) => {
      stdout += chunk.toString('utf8');
      // Cap captured output at 256 KB — anything past that is unread tail.
      if (stdout.length > 256 * 1024) {
        stdout = stdout.slice(-256 * 1024);
      }
    });
    proc.stderr?.on('data', () => { /* discard */ });
    const settle = (out: string | null) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timeoutHandle);
      resolve(out);
    };
    // Don't let a hung gcloud (slow Cloud Build log API, GCP network blip,
    // gcloud auth re-consent prompt) block `grid plug`. 15s is generous
    // for a tail fetch; if it hasn't replied by then, fall back silently
    // and let the caller print the gcloud-link fallback line.
    const timeoutHandle = setTimeout(() => {
      proc.kill();
      settle(null);
    }, 15_000);
    proc.on('error', () => settle(null));
    proc.on('close', (code) => {
      if (code === 0 && stdout.length > 0) settle(stdout);
      else settle(null);
    });
  });
};

/**
 * Pull the last `n` non-empty lines off captured gcloud stdout.
 * Trims trailing whitespace, drops blank lines (gcloud sometimes emits
 * empty trailers), and caps line length at 200 chars to keep wide build
 * output from blowing up the terminal.
 */
export function takeLastLines(raw: string, n: number): string[] {
  const lines = raw.split(/\r?\n/).map((l) => l.replace(/\s+$/, ''));
  // Drop blank trailers.
  while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
  const tail = lines.slice(-n);
  return tail.map((l) => (l.length > 200 ? l.slice(0, 197) + '...' : l));
}

/**
 * Render the boxed log tail as it should appear under the failed step line.
 * Header includes the build_id; footer always names the gcloud command for
 * full retrieval. Returns empty string when nothing useful to show.
 */
export function renderBuildLogTail(opts: {
  buildId: string;
  projectId: string;
  lines: string[];
}): string {
  const out: string[] = [];
  const idShort = opts.buildId.slice(0, 8);
  out.push(`  ┌─ last ${opts.lines.length} lines of build log (build_id ${idShort}...) ─`);
  for (const line of opts.lines) {
    out.push(`  │ ${line}`);
  }
  out.push(
    `  └─ full log: gcloud builds log ${opts.buildId} --project=${opts.projectId}`,
  );
  return out.join('\n');
}

/**
 * Full pipeline used by the plug verb on cloud_build failure. Returns a
 * ready-to-print string (no trailing newline) or null if there's nothing
 * useful to show. Accepts an injectable gcloud adapter so tests don't shell
 * out, and an optional override for the project id (defaults to unipier-ai).
 */
export async function buildLogTailFor(
  trace: DeployTrace,
  opts: {
    gcloudBuildsLog?: GcloudBuildsLogFn;
    projectId?: string;
    tailLines?: number;
  } = {},
): Promise<string | null> {
  // Only fire on cloud_build failures.
  const failed = trace.steps.find(
    (s) => s.status === 'failed' && s.step === 'cloud_build',
  );
  if (!failed) return null;

  const { buildId, consoleUrl } = resolveBuildId(trace);
  const projectId = opts.projectId ?? DEFAULT_GCLOUD_PROJECT;
  const tailLines = opts.tailLines ?? DEFAULT_TAIL_LINES;
  const adapter = opts.gcloudBuildsLog ?? realGcloudBuildsLog;

  if (!buildId) {
    if (consoleUrl) {
      return `  full log: ${consoleUrl}`;
    }
    return null;
  }

  const raw = await adapter(buildId, projectId);
  if (!raw) {
    return `  full log: gcloud builds log ${buildId} --project=${projectId}`;
  }

  const lines = takeLastLines(raw, tailLines);
  if (lines.length === 0) {
    return `  full log: gcloud builds log ${buildId} --project=${projectId}`;
  }

  return renderBuildLogTail({ buildId, projectId, lines });
}
